import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";

import type { KeyboardDnDHighlight } from "./keyboardDnDTypes";

/**
 * Keyboard DnD arena registry (design v2, architect-recommended mechanism).
 *
 * The per-canvas highlight machinery (deriveAllHighlightsFn closures,
 * compensators, the component-local highlight state) is unreachable from an
 * editor-level hook, so each dragging arena registers the capabilities the
 * keyboard modality needs. Registration is additive — the author-locked
 * mouse path (useAnvilDnDEvents.ts) is untouched.
 *
 * All redux drag-state operations (begin/end drag, active-canvas switch,
 * highlight-drawn bookkeeping) are implemented BY THE ARENA and exposed as
 * entry methods: the arena component already sits on the editor's import
 * graph, which keeps the keyboard controller free of app-wide imports (the
 * cyclic-dependency CI gate counts every module edge, type-only included).
 *
 * Entry members use method syntax deliberately: TS method parameters are
 * bivariant, so arenas may implement them with their richer concrete
 * highlight type while the registry stays on the structural
 * KeyboardDnDHighlight view.
 */
export interface KeyboardDnDArenaEntry {
  layoutId: string;
  widgetId: string;
  /** Hierarchy/overlay verdict from useAnvilDnDListenerStates. */
  canActivate: boolean;
  /** Whitelist/self-drop verdict from useAnvilDnDListenerStates. */
  allowToDrop: boolean;
  /** Derives + position-compensates all candidate highlights, in layout order. */
  getCompensatedHighlights(): KeyboardDnDHighlight[];
  /**
   * Renders (or clears) the insertion indicator on this arena and keeps the
   * shared highlight-drawn redux state in sync.
   */
  showHighlight(highlight: KeyboardDnDHighlight | null): void;
  /** Commits a drop via the arena's existing onDrop (moveAnvilWidgets). */
  onDrop(highlight: KeyboardDnDHighlight): void;
  /** Dispatches the same redux drag state the mouse/touch paths set. */
  beginDrag(widgetType: string): void;
  /** Ends the shared drag state (cancel/rollback/drop cleanup). */
  endDrag(): void;
  /** Makes this arena the active dragged-on canvas (Tab cycling). */
  makeActiveCanvas(): void;
}

interface KeyboardDnDRegistry {
  register: (entry: KeyboardDnDArenaEntry) => () => void;
  /** Entries in registration (mount) order. */
  getEntries: () => KeyboardDnDArenaEntry[];
  getEntry: (layoutId: string) => KeyboardDnDArenaEntry | undefined;
}

const KeyboardDnDRegistryContext = createContext<KeyboardDnDRegistry | null>(
  null,
);

export const useKeyboardDnDRegistry = () =>
  useContext(KeyboardDnDRegistryContext);

export const KeyboardDnDRegistryProvider = (props: { children: ReactNode }) => {
  const entriesRef = useRef<Map<string, KeyboardDnDArenaEntry>>(new Map());

  const register = useCallback((entry: KeyboardDnDArenaEntry) => {
    entriesRef.current.set(entry.layoutId, entry);

    return () => {
      // Only unregister if the stored entry is still ours — a re-register
      // for the same layoutId must not be clobbered by a stale cleanup.
      if (entriesRef.current.get(entry.layoutId) === entry) {
        entriesRef.current.delete(entry.layoutId);
      }
    };
  }, []);

  const getEntries = useCallback(
    () => Array.from(entriesRef.current.values()),
    [],
  );

  const getEntry = useCallback(
    (layoutId: string) => entriesRef.current.get(layoutId),
    [],
  );

  const value = useMemo(
    () => ({ register, getEntries, getEntry }),
    [register, getEntries, getEntry],
  );

  return (
    <KeyboardDnDRegistryContext.Provider value={value}>
      {props.children}
    </KeyboardDnDRegistryContext.Provider>
  );
};

/**
 * Registers an arena for keyboard DnD. Call from the arena component; the
 * entry is refreshed whenever its contents change and removed on unmount.
 */
export function useRegisterKeyboardDnDArena(entry: KeyboardDnDArenaEntry) {
  const registry = useKeyboardDnDRegistry();

  useEffect(
    function registerArenaForKeyboardDnD() {
      if (!registry) return;

      return registry.register(entry);
    },
    [registry, entry],
  );
}
